<template>
  <div
      ref="accessor"
      class="editable-table"
      style="text-align: left; justify-content: left"
  >
    <div class="handler" v-if="editable">
      <div
          class="drag-line"
          v-for="(pos, index) in dragLinePosition"
          :key="index"
          :style="{ left: pos + 'px' }"
          @mousedown="$event => handleMousedownColHandler($event, index)"
      ></div>
    </div>
    <table
        :class="{
        'theme': themeColor,
        'row-header': themeColor?.rowHeader,
        'row-footer': themeColor?.rowFooter,
        'col-header': themeColor?.colHeader,
        'col-footer': themeColor?.colFooter,
      }"
        :style="`--themeColor: ${themeColor?.color}; --subThemeColor1: ${subThemeColor[0]}; --subThemeColor2: ${subThemeColor[1]}`"
    >
      <colgroup>
        <col span="1" v-for="(width, index) in colSizeList" :key="index"
             :style="{
                ...handleFormulas('column', index),
                width:`${colWidths[index]}px`
             }">
      </colgroup>
      <tbody>
      <tr
          v-for="(rowCells, rowIndex) in tableCells"
          :key="rowIndex"
          :style="{ height: calcRowHeight(rowIndex) + 'px', ...handleFormulas('row', rowIndex) }"
      >
        <td
            class="cell"
            :class="{
              'selected': selectedCells.includes(`${rowIndex}_${colIndex}`) && selectedCells.length > 1,
              'active': activeCellIndex === `${rowIndex}_${colIndex}`,
            }"
            :style="{
              height: '1px',
              borderStyle: outline.style,
              borderColor: outline.color,
              borderWidth: outline.width + 'px',
              fontSize: cell.style?.fontSize,
              fontWeight: cell.style?.fontWeight,
              fontStyle: cell.style?.fontStyle,
              textDecoration: cell.style?.textDecoration,
              color: cell.style?.color,
              ...getTextStyle(cell.style),
              textAlign: cell.align?? 'left',
              ...(handleFormulas('cell', rowIndex, colIndex) || {})
            }"
            v-for="(cell, colIndex) in rowCells"
            :key="cell.id"
            :rowspan="cell.rowspan"
            :colspan="cell.colspan"
            :data-cell-index="`${rowIndex}_${colIndex}`"
            v-show="!hideCells.includes(`${rowIndex}_${colIndex}`)"
            @mousedown="$event => handleCellMousedown($event, rowIndex, colIndex)"
            @mouseenter="handleCellMouseenter(rowIndex, colIndex)"
            v-contextmenu="(el: HTMLElement) => contextmenus(el)"
            @focusin="handleCellFocusIn"
            @focusout="handleCellFocusOut"
        >
          <div
              :style="{
                  display: 'flex',
                  alignItems: cell.valign?? 'center',
                  justifyContent: cell.align?? 'left',
                  width: '100%',
                  height: '100%'
                }"
          >

            <ProsemirrorEditor
                :style="{
                  width: '100%',
                  textAlign: cell.align ?? 'left',
                  ...(handleFormulas('cell', rowIndex, colIndex) || {
                    display: 'flex',
                    flexDirection: positionFlex(cell)
                  })
                }"
                v-if="!hasModelData(cell) || handleFormulas('cell', rowIndex, colIndex)"
                class="text"
                elementId="abc"
                :editable="activeCellIndex === `${rowIndex}_${colIndex}`"
                :value="cell.text"
                @update="value => cell.text = value"
                defaultColor="#000000"
                defaultFontName="arial"
            />
            <div v-if="hasModelData(cell)" class="model-data-cell" :style="handleFormulas('cell', rowIndex, colIndex) ? handleFormulas('cell', rowIndex, colIndex) : {
                  display: 'flex',
                  flexDirection: positionFlex(cell)
              }">
              <div class="subset show-table-editor" :style="getHolderStyle(cell)"
                   v-if="showLabel(cell) && !labelAfter(cell)">
                <ProsemirrorEditor
                    class="text"
                    :class="{'active-editor': activeCellIndex === `${rowIndex}_${colIndex}`}"
                    elementId="abc"
                    :editable="activeCellIndex === `${rowIndex}_${colIndex}`"
                    :value="cell.text"
                    @update="value => cell.text = value"
                    defaultColor="#000000"
                    defaultFontName="arial"
                />
              </div>

              <ValueModelTableCell
                  v-if="cell.hookId"
                  :cell-data="cell"
                  :id="id + '-' + rowIndex + '_' + colIndex"
                  :active-cell="activeCellIndex === `${rowIndex}_${colIndex}`"
                  :filters="filters"
                  :cell-min-height="cellMinHeight"
                  :cell-row="rowIndex"
                  :cell-column="colIndex"
                  :table-cells="tableCells"
                  @change="emit('change', tableCells)"
                  @filteredValUpdate="formulasCache.clear()"
                  @click="showFormula"
              />

              <div class="subset show-table-editor" :style="getHolderStyle(cell)"
                   v-if="showLabel(cell) && labelAfter(cell)">
                <ProsemirrorEditor
                    class="text"
                    :class="{'active-editor': activeCellIndex === `${rowIndex}_${colIndex}`}"
                    elementId="abc"
                    :editable="activeCellIndex === `${rowIndex}_${colIndex}`"
                    :value="cell.text"
                    @update="value => cell.text = value"
                    defaultColor="#000000"
                    defaultFontName="arial"
                />
              </div>
            </div>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts" setup>
import {computed, nextTick, onMounted, onUnmounted, PropType, ref, watch} from 'vue'
import {debounce, isEqual} from 'lodash'
import {storeToRefs} from 'pinia'
import {nanoid} from 'nanoid'
import {useGivingTreeStore, useMainStore, useSlidesStore, useWidgetStore} from '@/store'
import {PPTElementOutline, TableCell, TableFilters, TableTheme} from '@/types/slides'
import {ContextmenuItem} from '@/components/Contextmenu/types'
import {KEYS} from '@/configs/hotkey'
import {getTextStyle} from './utils'
import useHideCells from './useHideCells'
import useSubThemeColor from './useSubThemeColor'
import ValueModelTableCell from "@/views/components/element/TableElement/ValueModelTableCell.vue";
import formula from "@jspreadsheet/formula-pro";
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue";
import {getValuePropositionAPI, ValuePropositionAPI} from "@/services/value-proposition-api";
import ProsemirrorEditor from "@/views/components/element/ProsemirrorEditor.vue";
import {applyRules} from "@/utils/common";

const accessor = ref()
const givingTreeStore = useGivingTreeStore()

const props = defineProps({
  data: {
    type: Array as PropType<TableCell[][]>,
    required: true,
  },
  id: {
    type: String
  },
  width: {
    type: Number,
    required: true,
  },
  cellMinHeight: {
    type: Number,
    required: true,
  },
  colWidths: {
    type: Array as PropType<number[]>,
    required: true,
  },
  rowHeights: {
    type: Array as PropType<number[]>,
    required: true,
  },
  outline: {
    type: Object as PropType<PPTElementOutline>,
    required: true,
  },
  theme: {
    type: Object as PropType<TableTheme>,
  },
  editable: {
    type: Boolean,
    default: true,
  },
  filters: {
    type: Array as PropType<TableFilters[]>
  }
})

formula.license('NTYzN2FkOGQ2NGU5YzliMGY4OGYzMjdhN2EyMWUxOTA4MTE5M2MwOTEyYWZhNmEyY2E1ZjNhNWI0YjI1OTEzYzUyYTBkY2JjMDE1ZThmOTAwNDcyYWJiODMwZWJlZjlhNTMyNDIxMTE4MWZhNDI0MWIxNmI5OThmNzhhZjNjNWQsZXlKamJHbGxiblJKWkNJNklqSmhaRGsyWTJNeE5tVTJNalV3TVRjek1qZGlPV0ZoT0RjM1ltSmtaR1ppTXpVek1tRTNNVGdpTENKdVlXMWxJam9pVW1GeGRXVnNJRWRoY21OcFlTSXNJbVJoZEdVaU9qRTNOVEF6TnpRd01EQXNJbVJ2YldGcGJpSTZXeUpzWlhabGNtRm5aWEJ2YVc1MExtTnZiU0lzSWpFeU55NHdMakF1TVRvNE9EZzRJaXdpTVRJM0xqQXVNQzR4SWl3aWQyVmlJaXdpYkc5allXeG9iM04wSWwwc0luQnNZVzRpT2lJek15SXNJbk5qYjNCbElqcGJJblkzSWl3aWRqZ2lMQ0oyT1NJc0luWXhNQ0lzSW5ZeE1TSXNJbVp2Y20xMWJHRWlMQ0ptYjNKdGN5SXNJbkpsYm1SbGNpSXNJbkJoY25ObGNpSXNJbWx0Y0c5eWRHVnlJaXdpZG1Gc2FXUmhkR2x2Ym5NaUxDSmpiMjF0Wlc1MGN5SXNJbk5sWVhKamFDSXNJbU5vWVhKMGN5SmRmUT09');

const emit = defineEmits<{
  (event: 'change', payload: TableCell[][]): void
  (event: 'changeColWidths', payload: number[]): void
  (event: 'changeSelectedCells', payload: string[]): void
}>()

const {canvasScale, tableCellHookId, tableFiltered} = storeToRefs(useMainStore())
const {selectedWidget, selectedTableCell} = storeToRefs(useWidgetStore())
const slidesStore = useSlidesStore()
const {currentSlide, rules, theme} = storeToRefs(slidesStore)

const isStartSelect = ref(false)
const startCell = ref<number[]>([])
const endCell = ref<number[]>([])
const focusOut = ref(false)

const {getActiveHookId, getComputedValue} = useComputedTreeValue()

onMounted(() => {
  checkWindowObjectExists(50, 'ValuePropositionAPI', async () => {
    const changeEventHandler = {
      handleEvent: () => {
        setTimeout(() => setRowCss(), 100)
      }
    }
    window['ValuePropositionAPI'].registerUIEvent('SLIDER_CHANGE', changeEventHandler)
  })

})

const calcRowHeight = (index) => {
  if (!props.rowHeights[index]) {
    props.rowHeights[index] = 0.5
  }

  return props.rowHeights[index] * 100
}

const hasModelData = (cell: TableCell) => {
  return cell.hookId && cell.mapping != 'none'
}

const labelMargins = (cell: TableCell) => {
  return cell.labelPosition && cell.labelPosition === 'left' || cell.labelPosition === 'right' ? '0 10px' : '10px 0'
}

const positionFlex = (cell: TableCell) => {
  return cell.labelPosition && cell.labelPosition === 'left' || cell.labelPosition === 'right' ? 'row' : 'column'
}

const labelAfter = (cell: TableCell) => {
  return cell.labelPosition && cell.labelPosition != 'top' && cell.labelPosition != 'left'
}

const showLabel = (cell: TableCell) => {
  return cell.labelPosition && cell.labelPosition !== 'none'
}

const getHolderStyle = (cell: TableCell) => {
  const style = {
    minWidth: '25px',
    margin: cell.labelPosition && cell.labelPosition === 'left' || cell.labelPosition === 'right' ? '0 10px' : '10px 0'
  }

  if (!cell.labelPosition || cell.labelPosition === 'top' || cell.labelPosition === 'bottom') {
    style.width = '100%'
  }
  return style
}

const tableCells = computed<TableCell[][]>({
  get() {
    return props.data
  },
  set(newData) {
    emit('change', newData)
  },
})

// theme secondary color
const themeColor = computed(() => props.theme)
const {subThemeColor} = useSubThemeColor(themeColor)

const fontFamily = computed(() => `"${theme.value.fontName}"` ?? '')

// Calculate the column width and total width of each column of the table
const colSizeList = ref<number[]>([])
const totalWidth = computed(() => colSizeList.value.reduce((a, b) => a + b))
watch([
  () => props.colWidths,
  () => props.width,
], () => {
  colSizeList.value = props.colWidths.map(item => item * props.width)
}, {immediate: true})

// Clear the selected state of all cells
// The table also needs to be cleared when it is in an uneditable state
const removeSelectedCells = () => {
  startCell.value = []
  endCell.value = []
}

watch(() => props.editable, () => {
  if (!props.editable) removeSelectedCells()
})

const showFormula = async () => {
  const api: ValuePropositionAPI | null = getValuePropositionAPI()

  const widget = givingTreeStore.getWidgetValue(tableCellHookId.value)


  if (widget.vpHasFormula) api?.fireUIEvent("varHandler", {action: "show_var", variableUid: widget.uid});

}

// Operation node position for dragging column width
const dragLinePosition = computed(() => {
  const dragLinePosition: number[] = []
  for (let i = 1; i < colSizeList.value.length + 1; i++) {
    const pos = colSizeList.value.slice(0, i).reduce((a, b) => (a + b))
    dragLinePosition.push(pos)
  }
  return dragLinePosition
})

// Collection of invalid cell positions (cell positions to be merged)
const cells = computed(() => props.data)
const {hideCells} = useHideCells(cells)

// The currently selected collection of cells
const selectedCells = computed(() => {
  if (!startCell.value.length) return []
  const [startX, startY] = startCell.value

  if (!endCell.value.length) return [`${startX}_${startY}`]
  const [endX, endY] = endCell.value

  if (startX === endX && startY === endY) return [`${startX}_${startY}`]

  const selectedCells = []

  const minX = Math.min(startX, endX)
  const minY = Math.min(startY, endY)
  const maxX = Math.max(startX, endX)
  const maxY = Math.max(startY, endY)

  for (let i = 0; i < tableCells.value.length; i++) {
    const rowCells = tableCells.value[i]
    for (let j = 0; j < rowCells.length; j++) {
      if (i >= minX && i <= maxX && j >= minY && j <= maxY) selectedCells.push(`${i}_${j}`)
    }
  }
  return selectedCells
})

watch(selectedCells, (value, oldValue) => {
  if (isEqual(value, oldValue)) return
  emit('changeSelectedCells', selectedCells.value)

  tableCellHookId.value = ''
  const cellData = getCellData()
  tableCellHookId.value = cellData.hookId

  if (startCell.value) {
    const [startX, startY] = startCell.value
    selectedTableCell.value = {row: startX, col: startY}
  } else {
    selectedTableCell.value = {}
  }
})

const getCellData = () => {
  let rowIndex = 0
  let colIndex = 0
  if (selectedCells.value.length) {
    const selectedCell = selectedCells.value[0]
    rowIndex = +selectedCell.split('_')[0]
    colIndex = +selectedCell.split('_')[1]
  }
  return props.data[rowIndex][colIndex]
}

// Currently active cell: If and only if there is only one selected cell, the cell is the active cell
const activeCellIndex = computed(() => {
  if (selectedCells.value.length > 1) return null
  return selectedCells.value[0]
})

// Set the selected cell state (mouse click or drag to select)
const handleMouseup = () => isStartSelect.value = false

const handleCellMousedown = (e: MouseEvent, rowIndex: number, colIndex: number) => {
  if (e.button === 0) {
    endCell.value = []
    isStartSelect.value = true
    startCell.value = [rowIndex, colIndex]
  }
}

const handleCellMouseenter = (rowIndex: number, colIndex: number) => {
  if (!isStartSelect.value) return
  endCell.value = [rowIndex, colIndex]
}

onMounted(() => {

  document.addEventListener('mouseup', handleMouseup)
})
onUnmounted(() => {
  document.removeEventListener('mouseup', handleMouseup)
})

// Determine whether a certain position is an invalid cell (the position that is merged)
const isHideCell = (rowIndex: number, colIndex: number) => hideCells.value.includes(`${rowIndex}_${colIndex}`)

// Select the specified column
const selectCol = (index: number) => {
  const maxRow = tableCells.value.length - 1
  startCell.value = [0, index]
  endCell.value = [maxRow, index]
}

// Select the specified row
const selectRow = (index: number) => {
  const maxCol = tableCells.value[index].length - 1
  startCell.value = [index, 0]
  endCell.value = [index, maxCol]
}

// Select all cells
const selectAll = () => {
  const maxRow = tableCells.value.length - 1
  const maxCol = tableCells.value[maxRow].length - 1
  startCell.value = [0, 0]
  endCell.value = [maxRow, maxCol]
}

// delete a line
const deleteRow = (rowIndex: number) => {
  const _tableCells: TableCell[][] = JSON.parse(JSON.stringify(tableCells.value))

  const targetCells = tableCells.value[rowIndex]
  const hideCellsPos = []
  for (let i = 0; i < targetCells.length; i++) {
    if (isHideCell(rowIndex, i)) hideCellsPos.push(i)
  }

  for (const pos of hideCellsPos) {
    for (let i = rowIndex; i >= 0; i--) {
      if (!isHideCell(i, pos)) {
        _tableCells[i][pos].rowspan = _tableCells[i][pos].rowspan - 1
        break
      }
    }
  }

  _tableCells.splice(rowIndex, 1)
  tableCells.value = _tableCells
}

// delete a column
const deleteCol = (colIndex: number) => {
  const _tableCells: TableCell[][] = JSON.parse(JSON.stringify(tableCells.value))

  const hideCellsPos = []
  for (let i = 0; i < tableCells.value.length; i++) {
    if (isHideCell(i, colIndex)) hideCellsPos.push(i)
  }

  for (const pos of hideCellsPos) {
    for (let i = colIndex; i >= 0; i--) {
      if (!isHideCell(pos, i)) {
        _tableCells[pos][i].colspan = _tableCells[pos][i].colspan - 1
        break
      }
    }
  }

  tableCells.value = _tableCells.map(item => {
    item.splice(colIndex, 1)
    return item
  })
  colSizeList.value.splice(colIndex, 1)
  emit('changeColWidths', colSizeList.value)
}

const emptyCell = (rowIndex: number, colIndex: number) => {
  const _tableCells: TableCell[][] = JSON.parse(JSON.stringify(tableCells.value))
  _tableCells[rowIndex][colIndex] = {
    colspan: 1,
    rowspan: 1,
    text: '',
    id: nanoid(10)
  }

  tableCells.value = _tableCells
}

// insert a row
const insertRow = (rowIndex: number) => {
  const _tableCells: TableCell[][] = JSON.parse(JSON.stringify(tableCells.value))

  const rowCells: TableCell[] = []
  for (let i = 0; i < _tableCells[0].length; i++) {
    rowCells.push({
      colspan: 1,
      rowspan: 1,
      text: '',
      id: nanoid(10),
    })
  }
  _tableCells.splice(rowIndex, 0, rowCells)
  tableCells.value = _tableCells
  selectedWidget.value.height = _tableCells.length * 36
}

// insert a column
const insertCol = (colIndex: number) => {
  tableCells.value = tableCells.value.map(item => {
    const cell = {
      colspan: 1,
      rowspan: 1,
      text: '',
      id: nanoid(10),
    }
    item.splice(colIndex, 0, cell)
    return item
  })
  colSizeList.value.splice(colIndex, 0, 100)
  emit('changeColWidths', colSizeList.value)
}

// Fill the specified number of rows/columns
const fillTable = (rowCount: number, colCount: number) => {
  let _tableCells: TableCell[][] = JSON.parse(JSON.stringify(tableCells.value))
  const defaultCell = {colspan: 1, rowspan: 1, text: ''}

  if (rowCount) {
    const newRows = []
    for (let i = 0; i < rowCount; i++) {
      const rowCells: TableCell[] = []
      for (let j = 0; j < _tableCells[0].length; j++) {
        rowCells.push({
          ...defaultCell,
          id: nanoid(10),
        })
      }
      newRows.push(rowCells)
    }
    _tableCells = [..._tableCells, ...newRows]
  }
  if (colCount) {
    _tableCells = _tableCells.map(item => {
      const cells: TableCell[] = []
      for (let i = 0; i < colCount; i++) {
        const cell = {
          ...defaultCell,
          id: nanoid(10),
        }
        cells.push(cell)
      }
      return [...item, ...cells]
    })
    colSizeList.value = [...colSizeList.value, ...new Array(colCount).fill(100)]
    emit('changeColWidths', colSizeList.value)
  }

  tableCells.value = _tableCells
}

// Merge Cells
const mergeCells = () => {
  const [startX, startY] = startCell.value
  const [endX, endY] = endCell.value

  const minX = Math.min(startX, endX)
  const minY = Math.min(startY, endY)
  const maxX = Math.max(startX, endX)
  const maxY = Math.max(startY, endY)

  const _tableCells: TableCell[][] = JSON.parse(JSON.stringify(tableCells.value))

  _tableCells[minX][minY].rowspan = maxX - minX + 1
  _tableCells[minX][minY].colspan = maxY - minY + 1

  tableCells.value = _tableCells
  removeSelectedCells()
}

// split cell
const splitCells = (rowIndex: number, colIndex: number) => {
  const _tableCells: TableCell[][] = JSON.parse(JSON.stringify(tableCells.value))
  _tableCells[rowIndex][colIndex].rowspan = 1
  _tableCells[rowIndex][colIndex].colspan = 1

  tableCells.value = _tableCells
  removeSelectedCells()
}

// Drag and drop to adjust column width
const handleMousedownColHandler = (e: MouseEvent, colIndex: number) => {
  removeSelectedCells()
  let isMouseDown = true

  const originWidth = colSizeList.value[colIndex]
  const startPageX = e.pageX

  const minWidth = 50

  document.onmousemove = e => {
    e.preventDefault()
    if (!isMouseDown) return

    const moveX = (e.pageX - startPageX) / canvasScale.value
    colSizeList.value[colIndex] = originWidth + moveX < minWidth ? minWidth : Math.round(originWidth + moveX)
  }
  document.onmouseup = () => {
    isMouseDown = false
    document.onmousemove = null
    document.onmouseup = null

    emit('changeColWidths', colSizeList.value)
  }
}

// Clear the text in the selected cell
const clearSelectedCellText = () => {
  const _tableCells: TableCell[][] = JSON.parse(JSON.stringify(tableCells.value))

  for (let i = 0; i < _tableCells.length; i++) {
    for (let j = 0; j < _tableCells[i].length; j++) {
      if (selectedCells.value.includes(`${i}_${j}`)) {
        _tableCells[i][j].text = ''
      }
    }
  }
  tableCells.value = _tableCells
}

// move focus to next cell
// When there is a cell to the right of the current row, the focus moves to the right
// When there is no cell on the right of the current row (already at the end of the row) and there is a next row, the focus moves to the beginning of the next row
// When there is no cell on the right of the current row (already at the end of the row) and there is no next row (already at the last row), create a new row and move the focus to the beginning of the next row
const tabActiveCell = () => {
  const getNextCell = (i: number, j: number): [number, number] | null => {
    if (!tableCells.value[i]) return null
    if (!tableCells.value[i][j]) return getNextCell(i + 1, 0)
    if (isHideCell(i, j)) return getNextCell(i, j + 1)
    return [i, j]
  }

  endCell.value = []

  const nextRow = startCell.value[0]
  const nextCol = startCell.value[1] + 1

  const nextCell = getNextCell(nextRow, nextCol)
  if (!nextCell) {
    insertRow(nextRow + 1)
    startCell.value = [nextRow + 1, 0]
  } else startCell.value = nextCell

  // Automatically focus text after moving focus
  nextTick(() => {
    const textRef = document.querySelector('.cell-text.active') as HTMLInputElement
    if (textRef) textRef.focus()
  })
}

const handleCellFocusOut = () => focusOut.value = false
const handleCellFocusIn = () => {
  focusOut.value = true
}

// Table shortcut key monitoring
const keydownListener = (e: KeyboardEvent) => {
  if (!props.editable || !selectedCells.value.length) return

  const key = e.key.toUpperCase()
  if (selectedCells.value.length < 2) {

    if (key === KEYS.TAB && focusOut.value) {
      e.preventDefault()
      tabActiveCell()
    }

    if (e.ctrlKey && key === KEYS.UP) {
      e.preventDefault()
      const rowIndex = +selectedCells.value[0].split('_')[0]
      insertRow(rowIndex)
    }
    if (e.ctrlKey && key === KEYS.DOWN) {
      e.preventDefault()
      const rowIndex = +selectedCells.value[0].split('_')[0]
      insertRow(rowIndex + 1)
    }
    if (e.ctrlKey && key === KEYS.LEFT) {
      e.preventDefault()
      const colIndex = +selectedCells.value[0].split('_')[1]
      insertCol(colIndex)
    }
    if (e.ctrlKey && key === KEYS.RIGHT) {
      e.preventDefault()
      const colIndex = +selectedCells.value[0].split('_')[1]
      insertCol(colIndex + 1)
    }
  } else if (key === KEYS.DELETE) {
    clearSelectedCellText()
  }
}

onMounted(() => {
  document.addEventListener('keydown', keydownListener)
})
onUnmounted(() => {
  document.removeEventListener('keydown', keydownListener)
})

// Update table data when cell text is entered
const handleInput = debounce(function (value, rowIndex, colIndex) {
  tableCells.value[rowIndex][colIndex].text = value
  emit('change', tableCells.value)
}, 300, {trailing: true})

// Insert data from Excel, and automatically complete when the number of rows/columns in the table is not enough
const insertExcelData = (data: string[][], rowIndex: number, colIndex: number) => {
  const maxRow = data.length
  const maxCol = data[0].length

  let fillRowCount = 0
  let fillColCount = 0
  if (rowIndex + maxRow > tableCells.value.length) fillRowCount = rowIndex + maxRow - tableCells.value.length
  if (colIndex + maxCol > tableCells.value[0].length) fillColCount = colIndex + maxCol - tableCells.value[0].length
  if (fillRowCount || fillColCount) fillTable(fillRowCount, fillColCount)

  nextTick(() => {
    for (let i = 0; i < maxRow; i++) {
      for (let j = 0; j < maxCol; j++) {
        if (tableCells.value[rowIndex + i][colIndex + j]) {
          tableCells.value[rowIndex + i][colIndex + j].text = data[i][j]
        }
      }
    }
    emit('change', tableCells.value)
  })
}

// Get valid cells (excluding merged cells)
const getEffectiveTableCells = () => {
  const effectiveTableCells = []

  for (let i = 0; i < tableCells.value.length; i++) {
    const rowCells = tableCells.value[i]
    const _rowCells = []
    for (let j = 0; j < rowCells.length; j++) {
      if (!isHideCell(i, j)) _rowCells.push(rowCells[j])
    }
    if (_rowCells.length) effectiveTableCells.push(_rowCells)
  }

  return effectiveTableCells
}

// Check if rows and columns can be deleted: valid number of rows/columns is greater than 1
const checkCanDeleteRowOrCol = () => {
  const effectiveTableCells = getEffectiveTableCells()
  const canDeleteRow = effectiveTableCells.length > 1
  const canDeleteCol = effectiveTableCells[0].length > 1

  return {canDeleteRow, canDeleteCol}
}

// check if merge or split is possible
// Multiple selections must be made to merge
// Must be single selected and the selected cell is a merged cell before it can be split
const checkCanMergeOrSplit = (rowIndex: number, colIndex: number) => {
  const isMultiSelected = selectedCells.value.length > 1
  const targetCell = tableCells.value[rowIndex][colIndex]

  const canMerge = isMultiSelected
  const canSplit = !isMultiSelected && (targetCell.rowspan > 1 || targetCell.colspan > 1)

  return {canMerge, canSplit}
}

const contextmenus = (el: HTMLElement): ContextmenuItem[] => {
  const cellIndex = el.dataset.cellIndex as string
  const rowIndex = +cellIndex.split('_')[0]
  const colIndex = +cellIndex.split('_')[1]

  if (!selectedCells.value.includes(`${rowIndex}_${colIndex}`)) {
    startCell.value = [rowIndex, colIndex]
    endCell.value = []
  }

  const {canMerge, canSplit} = checkCanMergeOrSplit(rowIndex, colIndex)
  const {canDeleteRow, canDeleteCol} = checkCanDeleteRowOrCol()

  return [
    {
      text: 'Insert Column',
      children: [
        {text: 'To the Left', handler: () => insertCol(colIndex)},
        {text: 'To the Right', handler: () => insertCol(colIndex + 1)},
      ],
    },
    {
      text: 'Insert Row',
      children: [
        {text: 'To the Top', handler: () => insertRow(rowIndex)},
        {text: 'To the Bottom', handler: () => insertRow(rowIndex + 1)},
      ],
    },
    {
      text: 'Delete Column',
      disable: !canDeleteCol,
      handler: () => deleteCol(colIndex),
    },
    {
      text: 'Delete Row',
      disable: !canDeleteRow,
      handler: () => deleteRow(rowIndex),
    },
    {divider: true},
    {
      text: 'Empty Cell',
      disable: selectedCells.value.length !== 1,
      handler: () => emptyCell(rowIndex, colIndex),
    },
    {divider: true},
    {
      text: 'Merge Cells',
      disable: !canMerge,
      handler: mergeCells,
    },
    {
      text: 'Unmerge Cells',
      disable: !canSplit,
      handler: () => splitCells(rowIndex, colIndex),
    },
    {divider: true},
    {
      text: 'Select Current Column',
      handler: () => selectCol(colIndex),
    },
    {
      text: 'Select Current Line',
      handler: () => selectRow(rowIndex),
    },
    {
      text: 'Select All Cells',
      handler: selectAll,
    },
  ]
}



const handleFormulas = (type: string, index: number, index2: number = -1) => {
  let filter: any;
  let results = {};
  if (props.filters) filter = findFilter(type, index, index2)

  if (filter) {
    results = {...applyRules(filter, rules.value)}
  }

  if (Object.keys(results).length === 0) {
    results = null;
  }

  if (type === 'row') {
    setRowCss(results, index);
  }

  if (results && type === 'column') {
    if (results.display) {
      results.visibility = 'collapse'
      delete results.display
    }

  }

  setRowCss()

  return results
}


watch(() => props.filters, () => {
  setRowCss()
});

const findFilter = (type: string, index: number, index2: number | null = null) => {
  return props.filters.find(item => {
    if (type === 'column') {
      return item.filterOn === type && item.columnIndex === index;
    } else if (type === 'row') {
      return item.filterOn === type && item.rowIndex === index;
    } else if (type === 'cell') {
      return item.filterOn === type && item.rowIndex === index && item.columnIndex === index2
    }
    return false;
  });
}

const setRowCss = (results = null, index = null) => {
  nextTick(() => {
    if (!accessor.value) {
      console.log('no accessor')
      return
    }

    const tables = accessor.value.querySelectorAll("table.theme");

    tables.forEach(function (table) {
      const rows = table.querySelectorAll("tr");


      let count = 0

      rows.forEach(function (row) {
        const showing = row.style.visibility !== 'collapse';

        if (showing) {
          row.classList.remove('manual-even', 'manual-odd');
          row.classList.add(count % 2 === 0 ? "manual-even" : "manual-odd");
          count++;

        } else {
          row.classList.remove('manual-even', 'manual-odd');
        }

      });

      if (!index || !rows[index]) return

      const row = rows[index];


      if (results) {
        if (results.color) {
          row.style.setProperty('--scenario-color', results.color);
        }

        if (results.backgroundColor) {
          row.style.setProperty('--scenario-bg-color', results.backgroundColor);
        }

        if (results.display) {
          row.style.setProperty('--scenario-vis', 'collapse')
        }
      } else {
        resetRowStyles(row);
      }
    });

  })
}

const resetRowStyles = (row) => {
  row.style.removeProperty('--scenario-color');
  row.style.removeProperty('--scenario-bg-color');
  row.style.removeProperty('--scenario-vis');
};



const checkWindowObjectExists = (ms, windowObject, resolution) => {
  if (window && window[windowObject]) {
    resolution()
  } else {
    setTimeout(() => checkWindowObjectExists(ms, windowObject, resolution), ms)
  }
}

</script>

<style lang="scss" scoped>
/* Alternate row colors */

.manual-even {
  background-color: var(--subThemeColor2);
}

.manual-odd {
  background-color: var(--subThemeColor1);
}

.editable-table {
  position: relative;
  user-select: none;
}

table {
  width: 100%;
  position: relative;
  table-layout: fixed;
  border-collapse: collapse;
  border-spacing: 0;
  border: 0;
  word-wrap: break-word;
  user-select: none;

  --themeColor: $themeColor;

  &.theme {
    background-color: #fff;
    font-family: v-bind(fontFamily);

    &.row-header {
      tr:first-child {
        background-color: var(--themeColor);
      }
    }

    &.row-footer {
      tr:last-child  {
        background-color: var(--themeColor);
      }
    }

  }

  .cell {
    position: relative;
    white-space: normal;
    word-wrap: break-word;
    vertical-align: middle;
    cursor: default;

    &.selected::after {
      content: '';
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      background-color: rgba($color: #666, $alpha: .4);
    }
  }

  .cell-text {
    padding: 5px;
    line-height: 1.5;
    user-select: none;
    cursor: text;
    display: flex;

    &.active {
      user-select: text;
    }
  }
}

.drag-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: $themeColor;
  margin-left: -1px;
  opacity: 0;
  z-index: 2;
  cursor: col-resize;
}

:deep(p) {
  margin: v-bind(labelMargins);
}

.model-data-cell {
  overflow: hidden;
}
</style>
